import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, Subject } from 'rxjs';
import { tap, shareReplay } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class CachedNetworkRequestService {
  private cache: Map<string, Observable<any>> = new Map();
  private inProgress: Map<string, Subject<any>> = new Map();

  constructor(private http: HttpClient) {}

  public get<T>(url: string): Observable<T> {
    // Check if the data is already cached
    if (this.cache.has(url)) {
      return this.cache.get(url)! as Observable<T>;
    }

    // Check if a call is in progress for this URL
    if (this.inProgress.has(url)) {
      return this.inProgress.get(url)!.asObservable() as Observable<T>;
    }

    // Create a Subject to handle multiple simultaneous subscribers
    const subject = new Subject<T>();
    this.inProgress.set(url, subject);

    // Perform the HTTP request
    const request = this.http.get<T>(url).pipe(
      tap(data => {
        // Cache the response and complete the Subject
        this.cache.set(url, of(data));
        subject.next(data);
        subject.complete();
        this.inProgress.delete(url);
      }),
      shareReplay(1), // Ensure that subsequent subscribers receive the same cached value
    );

    // Store the request observable in the cache
    this.cache.set(url, request);

    return request;
  }
}
